/**
 * View pdf2md output that the AIPs consumed (#56). Read-only Modal —
 * displays the markdown as preformatted text so the reviewer can spot
 * extraction issues that might explain a wrong AIP verdict.
 *
 * Loads lazily on Modal open via GET /reports/{id}/extracted-text.
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Modal,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components";
import { api } from "@/core/api/client";

interface Props {
  reportId: string;
  visible: boolean;
  onDismiss: () => void;
}

export function ExtractedTextModal({ reportId, visible, onDismiss }: Props) {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setText(null);
    api
      .get<{ text: string }>(`/reports/${reportId}/extracted-text`)
      .then((r) => {
        if (!cancelled) setText(r.text);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, reportId]);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Extracted text (pdf2md output)"
      size="large"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            Close
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <Box variant="small" color="text-body-secondary">
          This is the markdown that AIP-1 / AIP-3 / AIP-5 / AIP-6 read. Use it to sanity-check whether an
          ambiguous AI verdict was driven by a bad extraction.
        </Box>
        {loading && (
          <Box textAlign="center" padding="m">
            <Spinner /> Loading extracted text…
          </Box>
        )}
        {error && (
          <Alert type="error" header="Couldn't load extracted text">
            {error}
          </Alert>
        )}
        {text !== null && !loading && (
          <pre
            style={{
              maxHeight: "60vh",
              overflow: "auto",
              padding: 12,
              background: "var(--color-background-layout-toggle-default, #f4f4f4)",
              borderRadius: 6,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 12,
              lineHeight: 1.4,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              margin: 0,
            }}
          >
            {text}
          </pre>
        )}
      </SpaceBetween>
    </Modal>
  );
}
